/**
 * Article browser — main pane only. Sidebar (categories + recently-updated)
 * lives in ResearchLayout, which also owns the selected-category state.
 *
 * Filtering composes: the rail's selected category narrows the article
 * list first, then the search box's debounced needle (substring match on
 * title + summary) narrows it further. Match text inside each card is
 * highlighted via `<mark>`.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";

import { ResearchArticleIndexItem } from "../../../models/Research";
import { ALL_CATEGORY_ID } from "./CategoryRail";
import { useResearchLayoutContext } from "./ResearchLayout";
import { useResearchUser } from "./Research";

// Escape user input before inserting into a RegExp so a needle like
// "C++" or "$0.50" doesn't blow up the regex parser.
function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Wrap each case-insensitive occurrence of `needle` in `text` with a
 * `<mark>`. Implementation note: `String.split` with a capture group
 * puts the matched substring at every odd index in the result array,
 * which is the cheapest way to interleave matched / unmatched runs
 * without a manual walk.
 */
function highlightMatches(
  text: string | null | undefined,
  needle: string,
): React.ReactNode {
  const t = text ?? "";
  const trimmed = needle.trim();
  if (!trimmed || !t) return t;
  const re = new RegExp(`(${escapeRegex(trimmed)})`, "gi");
  const parts = t.split(re);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark
        key={i}
        // Soft yellow that doesn't fight the red/gray Trail Blazers
        // palette. `padding: 0` prevents the highlight from inflating
        // line height inside the card.
        style={{ backgroundColor: "#fff3a3", padding: 0 }}
      >
        {part}
      </mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    ),
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  // Display as e.g. "MAY 12" (uppercase to match mockup labels).
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d
    .toLocaleString("en-US", { month: "short", day: "2-digit" })
    .toUpperCase();
}

function ArticleCard({
  article,
  onClick,
  search,
}: {
  article: ResearchArticleIndexItem;
  onClick: () => void;
  /** Active search needle; empty string disables highlighting. */
  search: string;
}) {
  return (
    <Box
      onClick={onClick}
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        p: 2,
        mb: 1.5,
        cursor: "pointer",
        display: "flex",
        gap: 2,
        alignItems: "flex-start",
        "&:hover": {
          borderColor: "#bdbdbd",
          backgroundColor: "rgba(0,0,0,0.02)",
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          {article.category && (
            <Chip
              label={article.category.name}
              size="small"
              sx={{
                height: 22,
                borderRadius: "3px",
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.3px",
                color: "#fff",
                backgroundColor: article.category.color ?? "#6F6F6F",
                textTransform: "uppercase",
              }}
            />
          )}
          {article.status === "draft" && (
            <Chip
              label={`Draft · ${article.visibility ?? ""}`}
              size="small"
              sx={{
                height: 22,
                borderRadius: "3px",
                fontSize: "0.7rem",
                fontWeight: 600,
                backgroundColor: "#f0f0f0",
                color: "#666",
                textTransform: "uppercase",
              }}
            />
          )}
        </Stack>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          {highlightMatches(article.title, search)}
        </Typography>
        {article.summary && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {highlightMatches(article.summary, search)}
          </Typography>
        )}
        {article.author?.name && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1, color: "text.secondary" }}
          >
            {article.author.name}
          </Typography>
        )}
      </Box>
      <Box sx={{ flexShrink: 0, textAlign: "right" }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontSize: "0.7rem",
            fontWeight: 700,
            color: "text.secondary",
            letterSpacing: "0.3px",
          }}
        >
          UPDATED
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, letterSpacing: "0.3px" }}
        >
          {formatDate(article.last_edited_at)}
        </Typography>
      </Box>
    </Box>
  );
}

// Debounce so the in-memory filter + match-highlight regex rebuild
// doesn't fire on every keystroke. 150ms is the sweet spot we landed
// on in the original plan — short enough to feel instant, long enough
// to skip intermediate states when the user is typing fast.
const SEARCH_DEBOUNCE_MS = 150;

export default function ArticleList() {
  const navigate = useNavigate();
  const { loading, error, categories, articles, filterCategory } =
    useResearchLayoutContext();
  const { isAdmin } = useResearchUser();

  // The input value updates immediately for snappy typing; the
  // debounced value is what actually drives filtering + highlighting.
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const id = setTimeout(
      () => setDebouncedSearch(searchInput),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(id);
  }, [searchInput]);

  const categoryFiltered = useMemo(() => {
    if (filterCategory === ALL_CATEGORY_ID) return articles;
    return articles.filter((a) => a.category?.id === filterCategory);
  }, [articles, filterCategory]);

  // Two-stage filter: category first, then search. `categoryFiltered`
  // is the count we compare against in the "X of Y" hint when a search
  // is active.
  const filtered = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    if (!needle) return categoryFiltered;
    return categoryFiltered.filter((a) => {
      const hay = [a.title, a.summary]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [categoryFiltered, debouncedSearch]);

  const selectedCategoryName = useMemo(() => {
    if (filterCategory === ALL_CATEGORY_ID) return "All Articles";
    const cat = categories.find((c) => c.id === filterCategory);
    return cat?.name ?? "Articles";
  }, [filterCategory, categories]);

  const hasActiveSearch = debouncedSearch.trim().length > 0;
  const countLabel = hasActiveSearch
    ? `${filtered.length} of ${categoryFiltered.length} matching “${debouncedSearch.trim()}”`
    : `${filtered.length} ${filtered.length === 1 ? "article" : "articles"}`;

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 0.5 }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {selectedCategoryName}
        </Typography>
        {isAdmin && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate("/research/new")}
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            New article
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {countLabel}
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Search articles by title or summary…"
        value={searchInput}
        onChange={(e) => setSearchInput(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "text.secondary", fontSize: "1.1rem" }} />
            </InputAdornment>
          ),
          endAdornment: searchInput && (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Clear search"
                onClick={() => setSearchInput("")}
                edge="end"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Divider sx={{ mb: 2 }} />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && filtered.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
          {hasActiveSearch
            ? `No articles match “${debouncedSearch.trim()}”.`
            : "No articles in this category yet."}
        </Typography>
      )}

      {!loading &&
        !error &&
        filtered.map((article) => (
          <ArticleCard
            key={article.id}
            article={article}
            search={debouncedSearch}
            onClick={() => navigate(`/research/${article.slug}`)}
          />
        ))}
    </>
  );
}
